import { useState, useRef, type FormEvent } from "react";
import { retentionCeilingAmount, formatRetentionCeilingPercent, parseRetentionCeilingPercentInput } from "../lib/retentionCeiling";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  X,
  CheckCircle,
  Upload,
  Download,
  Loader2,
  RefreshCw,
  AlertCircle,
  ImagePlus,
  Globe,
} from "lucide-react";
import { supabase, requireOrganizationId } from "../lib/supabase";
import {
  DEFAULT_PAYMENT_PERIOD_DAYS,
  resolveClaimDay,
  resolvePaymentPeriodDays,
} from "../lib/claimTiming";
import { claimTimingDb } from "../lib/claimTimingDb";
import { useAuth } from "../hooks/useAuth";
import {
  DEFAULT_CATEGORIES,
  DEFAULT_SUBCATEGORIES,
  formatCurrency,
} from "../lib/calculations";
import {
  parseCSV,
  transformCSVToProjectData,
  validateProjectData,
} from "../lib/csvImport";
import { useProjectLocations } from "../hooks/useDashpivot";
import { useExistingProjectNames } from "../hooks/useProjects";
import { TemplateGuideModal } from "../components/TemplateGuideModal";
import { WebDescriptionEditor } from "../components/WebDescriptionEditor";
import { ProjectPeopleFields } from "../components/ProjectPeopleFields";
import { ProjectConfirmationModal } from "../components/ProjectConfirmationModal";
import { useSubscription } from "../hooks/useSubscription";
import { useAuditLog } from "../hooks/useAuditLog";
import type { ParsedProjectData } from "../lib/csvImport";
import { uploadProjectImage } from "../lib/projectImages";
import { logWarn } from "../lib/logError";

interface CategoryInput {
  name: string;
  isCustomAmountOnly: boolean;
  customAmount: string;
  subcategories: { name: string; amount: string }[];
}

interface DiscountInput {
  description: string;
  amount: string;
}

// Component for Job Location field with Dashpivot integration
function DashpivotLocationField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { names: existingNames } = useExistingProjectNames();
  const { locations, loading, error, isConfigured, refetch } =
    useProjectLocations(existingNames);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // If Dashpivot not configured or user chose manual entry, show text input
  if (!isConfigured || isManualEntry) {
    return (
      <div className="form-group">
        <label htmlFor="jobLocation" className="form-label">
          Job Location / Project Name *
        </label>
        <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
          <input
            id="jobLocation"
            type="text"
            className="form-input"
            placeholder="e.g., Brisbane CBD Tower"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required
            style={{ flex: 1 }}
          />
          {isConfigured && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setIsManualEntry(false)}
              title="Switch to Dashpivot selection"
              style={{ padding: "var(--spacing-sm)" }}
            >
              <RefreshCw size={18} />
            </button>
          )}
        </div>
        {!isConfigured && (
          <p
            style={{
              fontSize: "0.75rem",
              color: "var(--text-secondary)",
              marginTop: "var(--spacing-xs)",
            }}
          >
            <AlertCircle
              size={12}
              style={{ display: "inline", marginRight: 4 }}
            />
            Dashpivot not configured. Using manual entry.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="form-group">
      <label htmlFor="jobLocation" className="form-label">
        Job Location / Project Name *
      </label>
      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
          {loading ? (
            <div
              className="form-input"
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: "var(--spacing-sm)",
                color: "var(--text-secondary)",
              }}
            >
              <Loader2 size={16} className="animate-spin" />
              Loading from Dashpivot...
            </div>
          ) : error ? (
            <div
              className="form-input"
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: "var(--spacing-sm)",
                color: "var(--danger)",
              }}
            >
              <AlertCircle size={16} />
              {error}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={refetch}
                style={{ marginLeft: "auto", padding: "4px" }}
              >
                <RefreshCw size={14} />
              </button>
            </div>
          ) : (
            <div style={{ flex: 1, position: "relative" }}>
              <input
                id="jobLocation"
                type="text"
                className="form-input"
                placeholder="Select or type project name..."
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                required
                autoComplete="off"
              />
              {showDropdown && locations.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    background: "var(--bg-primary)",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "var(--radius-md)",
                    boxShadow: "var(--shadow-lg)",
                    maxHeight: 200,
                    overflowY: "auto",
                    zIndex: 100,
                  }}
                >
                  {locations
                    .filter((loc) =>
                      loc.name.toLowerCase().includes(value.toLowerCase()),
                    )
                    .map((location) => (
                      <button
                        key={location.id}
                        type="button"
                        onClick={() => {
                          onChange(location.name);
                          setShowDropdown(false);
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          padding: "var(--spacing-sm) var(--spacing-md)",
                          textAlign: "left",
                          background:
                            value === location.name
                              ? "var(--bg-tertiary)"
                              : "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--text-primary)",
                          fontSize: "0.875rem",
                        }}
                        onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "var(--bg-secondary)")
                        }
                        onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          value === location.name
                            ? "var(--bg-tertiary)"
                            : "transparent")
                        }
                      >
                        {location.name}
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setIsManualEntry(true)}
            title="Enter manually"
            style={{ padding: "var(--spacing-sm)" }}
          >
            <Plus size={18} />
          </button>
        </div>
        {locations.length > 0 && (
          <p
            style={{
              fontSize: "0.75rem",
              color: "var(--text-secondary)",
              marginTop: "var(--spacing-xs)",
            }}
          >
            {locations.length} projects from Dashpivot • Type to filter or click{" "}
            <Plus size={10} style={{ display: "inline" }} /> for custom entry
          </p>
        )}
      </div>
    </div>
  );
}

export function NewProjectPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  // SEQ-169 follow up. Same expression as RoleProtectedWrapper and ProjectDetail,
  // so the app keeps one definition of financial access. This route is not
  // wrapped in RoleProtectedWrapper because creating a project is not itself a
  // financial act; only the claim timing block inside it is.
  const hasFinancialAccess = Boolean(profile?.can_view_financials);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [step, setStep] = useState<"details" | "categories">("details");

  // Project details
  const [jobLocation, setJobLocation] = useState("");
  const [jobAddress, setJobAddress] = useState("");
  const [projectDetails, setProjectDetails] = useState("");
  // Public website fields (separate from internal project_details).
  const [webDescription, setWebDescription] = useState("");
  const [showOnWeb, setShowOnWeb] = useState(true);
  const [webTitle, setWebTitle] = useState("");
  const [webSuburb, setWebSuburb] = useState("");
  const [webRegion, setWebRegion] = useState("");
  const [webType, setWebType] = useState("");
  const [normalRate, setNormalRate] = useState("100");
  const [overtimeRate, setOvertimeRate] = useState("150");
  const [saturdayRate, setSaturdayRate] = useState("400");
  const [saturdayContractOTRate, setSaturdayContractOTRate] = useState("");
  const [claimDay, setClaimDay] = useState("26");
  // SEQ-169: seeded at the value that was hardcoded in the claim modal, so a
  // new project claims on the same terms it would have before this change.
  const [paymentPeriodDays, setPaymentPeriodDays] = useState(
    DEFAULT_PAYMENT_PERIOD_DAYS.toString(),
  );
  const [retentionCeilingMode, setRetentionCeilingMode] = useState<"original" | "revised" | "custom">("original");
  const [retentionCustomAmount, setRetentionCustomAmount] = useState("");
  const [retentionCeilingPercent, setRetentionCeilingPercent] = useState("5");
  const [status, setStatus] = useState<"active" | "completed" | "on_hold" | "upcoming">(
    "active",
  );
  const [builderName, setBuilderName] = useState("");
  const [builderAddress, setBuilderAddress] = useState("");
  // SEQ-180 phase 2, P22.
  const [projectManager, setProjectManager] = useState("");
  const [siteManager, setSiteManager] = useState("");
  const [builderPocName, setBuilderPocName] = useState("");
  const [builderEmail, setBuilderEmail] = useState("");

  // Modal states
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [importedData, setImportedData] = useState<ParsedProjectData | null>(
    null,
  );

  // Dashpivot Projects for validation
  const { names: existingNames } = useExistingProjectNames();
  const { locations, loading: loadingLocations } =
    useProjectLocations(existingNames);

  // Categories - start with defaults
  const [categories, setCategories] = useState<CategoryInput[]>(
    DEFAULT_CATEGORIES.map((name) => ({
      name,
      isCustomAmountOnly: false,
      customAmount: "",
      subcategories: DEFAULT_SUBCATEGORIES.map((subName) => ({
        name: subName,
        amount: "",
      })),
    })),
  );

  // Discounts
  const [discounts, setDiscounts] = useState<DiscountInput[]>([]);

  // Project images (staged locally before project creation)
  const [stagedImages, setStagedImages] = useState<File[]>([]);
  const [stagedPreviews, setStagedPreviews] = useState<string[]>([]);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Tier enforcement + audit
  const { canCreateProject } = useSubscription();
  const { log: auditLog } = useAuditLog();

  // Category handlers
  const handleAddCategory = () => {
    setCategories([
      ...categories,
      {
        name: "",
        isCustomAmountOnly: false,
        customAmount: "",
        subcategories: [],
      },
    ]);
  };

  const handleRemoveCategory = (index: number) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  const handleCategoryChange = (
    index: number,
    field: keyof CategoryInput,
    value: string | boolean,
  ) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], [field]: value };
    setCategories(updated);
  };

  const handleToggleCategoryMode = (catIndex: number) => {
    const updated = [...categories];
    updated[catIndex].isCustomAmountOnly =
      !updated[catIndex].isCustomAmountOnly;
    // If switching to subcategories and none exist, add default ones
    if (
      !updated[catIndex].isCustomAmountOnly &&
      updated[catIndex].subcategories.length === 0
    ) {
      updated[catIndex].subcategories = DEFAULT_SUBCATEGORIES.slice(0, 5).map(
        (subName) => ({
          name: subName,
          amount: "",
        }),
      );
    }
    setCategories(updated);
  };

  // Subcategory handlers
  const handleSubcategoryChange = (
    catIndex: number,
    subIndex: number,
    field: "name" | "amount",
    value: string,
  ) => {
    const updated = [...categories];
    updated[catIndex].subcategories[subIndex] = {
      ...updated[catIndex].subcategories[subIndex],
      [field]: value,
    };
    setCategories(updated);
  };

  const handleAddSubcategory = (catIndex: number) => {
    const updated = [...categories];
    updated[catIndex].subcategories.push({ name: "", amount: "" });
    setCategories(updated);
  };

  const handleRemoveSubcategory = (catIndex: number, subIndex: number) => {
    const updated = [...categories];
    updated[catIndex].subcategories = updated[catIndex].subcategories.filter(
      (_, i) => i !== subIndex,
    );
    setCategories(updated);
  };

  const handleAddSubcategoryFromList = (catIndex: number, subName: string) => {
    const updated = [...categories];
    if (!updated[catIndex].subcategories.some((s) => s.name === subName)) {
      updated[catIndex].subcategories.push({ name: subName, amount: "" });
    }
    setCategories(updated);
  };

  // Discount Handlers
  const handleAddDiscount = () => {
    setDiscounts([...discounts, { description: "", amount: "" }]);
  };

  const handleRemoveDiscount = (index: number) => {
    setDiscounts(discounts.filter((_, i) => i !== index));
  };

  const handleDiscountChange = (
    index: number,
    field: keyof DiscountInput,
    value: string,
  ) => {
    const updated = [...discounts];
    updated[index] = { ...updated[index], [field]: value };
    setDiscounts(updated);
  };

  // Calculate category total
  const getCategoryTotal = (category: CategoryInput): number => {
    if (category.isCustomAmountOnly) {
      return parseFloat(category.customAmount) || 0;
    }
    return category.subcategories.reduce(
      (sum, sub) => sum + (parseFloat(sub.amount) || 0),
      0,
    );
  };

  // Calculate total contract value (Categories - Discounts)
  const calculateTotalContractValue = () => {
    const categoriesTotal = categories.reduce(
      (total, cat) => total + getCategoryTotal(cat),
      0,
    );
    const discountsTotal = discounts.reduce(
      (total, d) => total + (parseFloat(d.amount) || 0),
      0,
    );
    return categoriesTotal - discountsTotal;
  };

  // Handle CSV file import
  const handleFileImport = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setImportSuccess(null);

    try {
      const text = await file.text();
      // Only parse first to validate structure
      const lines = text.trim().split("\n");
      // Skip comments
      const csvContent = lines
        .filter((line) => !line.startsWith("#"))
        .join("\n");
      const rows = parseCSV(csvContent);
      const projectData = transformCSVToProjectData(rows);
      const validationErrors = validateProjectData(projectData);

      if (validationErrors.length > 0) {
        setError(validationErrors.join("; "));
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      // Store data and open confirmation
      setImportedData(projectData);
      setIsConfirmationOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to parse CSV file");
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const finalizeImport = (projectName: string) => {
    if (!importedData) return;

    // Populate form with imported data using confirmed project name
    setJobLocation(projectName);
    setJobAddress(importedData.job_address);
    setProjectDetails(importedData.project_details);
    setNormalRate(importedData.normal_rate.toString());
    setOvertimeRate(importedData.overtime_rate.toString());
    setSaturdayRate(importedData.saturday_rate.toString());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- saturday_contract_ot_rate may not be typed in ParshedProjectData
    if ((importedData as any).saturday_contract_ot_rate) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see above
      setSaturdayContractOTRate((importedData as any).saturday_contract_ot_rate.toString());
    }

    // Set status and builder details
    const statusVal = importedData.status as "active" | "completed" | "on_hold" | "upcoming";
    setStatus(
      ["active", "completed", "on_hold", "upcoming"].includes(statusVal)
        ? statusVal
        : "active",
    );
    setBuilderName(importedData.builder_name || "");
    setBuilderAddress(importedData.builder_address || "");
    setBuilderPocName(importedData.builder_poc_name || "");
    setBuilderEmail(importedData.builder_email || "");

    // Convert categories to form format
    const importedCategories: CategoryInput[] = importedData.categories.map(
      (cat) => ({
        name: cat.name,
        isCustomAmountOnly: cat.isFixedAmount,
        customAmount: cat.isFixedAmount ? cat.fixedAmount.toString() : "",
        subcategories: cat.subcategories.map((sub) => ({
          name: sub.name,
          amount: sub.amount.toString(),
        })),
      }),
    );

    setCategories(importedCategories);

    // Import Discounts
    if (importedData.discounts) {
      setDiscounts(
        importedData.discounts.map((d) => ({
          description: d.description,
          amount: d.amount.toString(),
        })),
      );
    }

    // Count fixed amount categories
    const fixedAmountCount = importedData.categories.filter(
      (c) => c.isFixedAmount,
    ).length;

    const discountCount = importedData.discounts
      ? importedData.discounts.length
      : 0;

    setImportSuccess(
      `Imported ${importedData.categories.length} categories (${fixedAmountCount} fixed amount) and ${discountCount} discounts`,
    );
    setStep("categories");
    setIsConfirmationOpen(false);
    setImportedData(null);
  };

  // Submit handler
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // SEQ-235: only a user with financial access can set a ceiling other than 5.
    const pctInput = parseRetentionCeilingPercentInput(retentionCeilingPercent);
    if (hasFinancialAccess && !pctInput.ok) {
      setError(pctInput.message);
      return;
    }
    setLoading(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create project
      const orgId = requireOrganizationId();

      // Tier enforcement: check project limit
      const { count: projectCount } = await supabase
        .from("claims_projects")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", orgId);

      if (!canCreateProject(projectCount ?? 0)) {
        throw new Error("Project limit reached for your current plan. Please upgrade to create more projects.");
      }

      const { data: project, error: projectError } = await supabase
        .from("claims_projects")
        .insert({
          organization_id: orgId,
          job_location: jobLocation,
          job_address: jobAddress,
          project_details: projectDetails,
          web_description: webDescription || null,
          show_on_web: showOnWeb,
          web_title: webTitle || null,
          web_suburb: webSuburb || null,
          web_region: webRegion || null,
          web_type: webType || null,
          normal_rate: parseFloat(normalRate),
          overtime_rate: parseFloat(overtimeRate),
          saturday_rate: parseFloat(saturdayRate),
          saturday_contract_ot_rate: saturdayContractOTRate ? parseFloat(saturdayContractOTRate) : null,
          claim_day: resolveClaimDay(claimDay, 26),
          retention_on_revised: retentionCeilingMode === 'revised',
          retention_ceiling_mode: retentionCeilingMode,
          retention_custom_amount: retentionCeilingMode === 'custom' ? (parseFloat(retentionCustomAmount) || null) : null,
          ...(hasFinancialAccess && pctInput.ok ? { retention_ceiling_percent: pctInput.value } : {}),
          status: status,
          builder_name: builderName || null,
          builder_address: builderAddress || null,
          builder_poc_name: builderPocName || null,
          builder_email: builderEmail || null,
          project_manager: projectManager.trim() || null,
          site_manager: siteManager.trim() || null,
          created_by: user.id,
        })
        .select()
        .single();

      if (projectError) throw projectError;

      // SEQ-169 claim timing. Every project a financial user creates gets a
      // config row, otherwise the claim modal falls back to the organisation
      // setting and the settings screen has nothing to edit. claim_day above is
      // written with the same resolver, so the mirror the database trigger
      // maintains agrees with this row from the moment the project exists.
      //
      // SEQ-169 follow up: skipped without financial access. The fields are not
      // shown to that user, so the row would only ever carry the defaults, and
      // the RLS policy set by 20260915000200 refuses the insert anyway. Leaving
      // the row out puts the project on the documented organisation setting
      // fallback, which ProgressClaimModal already handles with maybeSingle,
      // rather than failing project creation after the project row exists.
      if (hasFinancialAccess) {
        const { data: savedTiming, error: timingError } = await claimTimingDb
          .from("claims_project_config")
          .insert({
            project_id: project.id,
            organization_id: orgId,
            default_claim_day: resolveClaimDay(claimDay, 26),
            payment_period_days: resolvePaymentPeriodDays(paymentPeriodDays),
          })
          .select("project_id")
          .maybeSingle();

        // The error alone is not enough. A write that RLS filters to zero rows
        // is reported as a success by supabase-js, so the returned row is what
        // proves the claim timing actually landed.
        if (timingError) throw timingError;
        if (!savedTiming) {
          throw new Error(
            "The project was created but its claim timing was not saved. You may not have permission to set claim timing. Set the claim day and payment period from Edit Project before raising a claim.",
          );
        }
      }

      // Create categories and subcategories
      for (let i = 0; i < categories.length; i++) {
        const cat = categories[i];
        if (!cat.name.trim()) continue;

        const { data: category, error: catError } = await supabase
          .from("claims_categories")
          .insert({
            organization_id: orgId,
            project_id: project.id,
            name: cat.name,
            is_default: DEFAULT_CATEGORIES.includes(cat.name),
            is_custom_amount_only: cat.isCustomAmountOnly,
            custom_amount: cat.isCustomAmountOnly
              ? parseFloat(cat.customAmount) || 0
              : null,
            sort_order: i,
          })
          .select()
          .single();

        if (catError) throw catError;

        // Insert subcategories
        if (!cat.isCustomAmountOnly && cat.subcategories.length > 0) {
          const subcats = cat.subcategories
            .filter((sub) => sub.name.trim())
            .map((sub, subIndex) => ({
              organization_id: orgId,
              category_id: category.id,
              name: sub.name,
              amount: parseFloat(sub.amount) || 0,
              is_default: DEFAULT_SUBCATEGORIES.includes(sub.name),
              sort_order: subIndex,
            }));

          if (subcats.length > 0) {
            const { error: subError } = await supabase
              .from("claims_subcategories")
              .insert(subcats);

            if (subError) throw subError;
          }
        } else if (cat.isCustomAmountOnly) {
          // Create FIXED AMOUNT subcategory for fixed amount categories
          const { error: subError } = await supabase
            .from("claims_subcategories")
            .insert({
              organization_id: orgId,
              category_id: category.id,
              name: "FIXED AMOUNT",
              amount: parseFloat(cat.customAmount) || 0,
              is_default: false,
              sort_order: 0,
            });

          if (subError) throw subError;
        }
      }

      // Insert Discounts
      if (discounts.length > 0) {
        const discountInserts = discounts
          .filter((d) => d.description.trim() && d.amount)
          .map((d) => ({
            organization_id: orgId,
            project_id: project.id,
            description: d.description,
            amount: parseFloat(d.amount) || 0,
          }));

        if (discountInserts.length > 0) {
          const { error: discError } = await supabase
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
            .from("claims_discounts" as any)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
            .insert(discountInserts as any);

          if (discError) throw discError;
        }
      }

      // Audit log
      auditLog({
        action: "project.create",
        entity_type: "project",
        entity_id: project.id,
        metadata: { name: jobLocation, categories_count: categories.length },
      });

      // Upload staged images (non-blocking, don't fail project creation)
      if (stagedImages.length > 0) {
        try {
          for (let imgIdx = 0; imgIdx < stagedImages.length; imgIdx++) {
            await uploadProjectImage(project.id, stagedImages[imgIdx], {
              isCover: imgIdx === 0,
            });
          }
        } catch (imgErr) {
          logWarn("Some images failed to upload:", imgErr);
        }
      }

      navigate(`/projects/${project.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create project");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "var(--spacing-xl)" }}>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/projects")}
          style={{ marginBottom: "var(--spacing-md)" }}
        >
          <ArrowLeft size={18} />
          Back to Projects
        </button>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "var(--spacing-md)",
          }}
        >
          <h1>Create New Project</h1>
          <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setIsGuideOpen(true)}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <Download size={16} />
              Download Template
            </button>
            <label
              className="btn btn-secondary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
              }}
            >
              <Upload size={16} />
              Import CSV
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileImport}
                style={{ display: "none" }}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="card" style={{ marginBottom: "var(--spacing-xl)" }}>
        <div style={{ display: "flex", gap: "var(--spacing-lg)" }}>
          <button
            className={`btn ${step === "details" ? "btn-primary" : "btn-ghost"
              }`}
            onClick={() => setStep("details")}
          >
            1. Project Details
          </button>
          <button
            className={`btn ${step === "categories" ? "btn-primary" : "btn-ghost"
              }`}
            onClick={() => setStep("categories")}
            disabled={!jobLocation || !jobAddress}
          >
            2. Categories & Amounts
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {step === "details" && (
          <div className="card">
            <h3 style={{ marginBottom: "var(--spacing-lg)" }}>
              Project Details
            </h3>

            <DashpivotLocationField
              value={jobLocation}
              onChange={setJobLocation}
            />

            <div className="form-group">
              <label htmlFor="jobAddress" className="form-label">
                Job Address *
              </label>
              <input
                id="jobAddress"
                type="text"
                className="form-input"
                placeholder="e.g., 123 Queen Street, Brisbane"
                value={jobAddress}
                onChange={(e) => setJobAddress(e.target.value)}
                required
              />
            </div>

            {/* ── PROJECT IMAGES (optional) ── */}
            <div className="form-group">
              <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <ImagePlus size={16} />
                Project Photos
                <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 400 }}>
                  (optional)
                </span>
              </label>

              {/* Staged image previews */}
              {stagedPreviews.length > 0 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))",
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  {stagedPreviews.map((preview, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: "relative",
                        aspectRatio: "1",
                        borderRadius: 8,
                        overflow: "hidden",
                        border: idx === 0 ? "2px solid oklch(0.75 0.15 85)" : "1px solid var(--border-color)",
                      }}
                    >
                      <img
                        src={preview}
                        alt={`Staged ${idx + 1}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          URL.revokeObjectURL(preview);
                          setStagedImages(prev => prev.filter((_, i) => i !== idx));
                          setStagedPreviews(prev => prev.filter((_, i) => i !== idx));
                        }}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          border: "none",
                          background: "oklch(0.15 0 0 / 0.6)",
                          color: "oklch(0.95 0 0)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          padding: 0,
                          fontSize: 12,
                        }}
                      >
                        <X size={12} />
                      </button>
                      {idx === 0 && (
                        <span
                          style={{
                            position: "absolute",
                            bottom: 3,
                            left: 3,
                            background: "oklch(0.75 0.15 85)",
                            borderRadius: 3,
                            padding: "1px 5px",
                            fontSize: "0.55rem",
                            fontWeight: 700,
                            color: "oklch(0.15 0 0)",
                            textTransform: "uppercase",
                          }}
                        >
                          Cover
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Upload area */}
              <div
                onClick={() => imageInputRef.current?.click()}
                onDrop={(e) => {
                  e.preventDefault();
                  const files = Array.from(e.dataTransfer.files).filter(f =>
                    ["image/jpeg", "image/png", "image/webp", "image/avif"].includes(f.type)
                  );
                  if (files.length > 0) {
                    setStagedImages(prev => [...prev, ...files]);
                    setStagedPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
                style={{
                  height: 80,
                  borderRadius: 10,
                  border: "2px dashed var(--border-color)",
                  background: "var(--bg-tertiary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  cursor: "pointer",
                  transition: "border-color 0.2s ease",
                  fontSize: "0.8rem",
                  color: "var(--text-secondary)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.55 0.08 250)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "var(--border-color)";
                }}
              >
                <ImagePlus size={18} style={{ color: "var(--text-muted)" }} />
                Drop images or click to browse
              </div>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/avif"
                multiple
                onChange={(e) => {
                  if (e.target.files) {
                    const files = Array.from(e.target.files);
                    setStagedImages(prev => [...prev, ...files]);
                    setStagedPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
                    e.target.value = "";
                  }
                }}
                style={{ display: "none" }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="projectDetails" className="form-label">
                Project Details
              </label>
              <textarea
                id="projectDetails"
                className="form-input"
                rows={3}
                placeholder="Additional project information..."
                value={projectDetails}
                onChange={(e) => setProjectDetails(e.target.value)}
              />
            </div>

            {/* ── PUBLIC WEBSITE ── */}
            <div
              className="form-group"
              style={{ borderTop: "1px solid var(--border-color)", paddingTop: "var(--spacing-lg)" }}
            >
              <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Globe size={16} /> Public website
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: "pointer",
                  marginBottom: "var(--spacing-md)",
                }}
              >
                <input
                  type="checkbox"
                  checked={showOnWeb}
                  onChange={(e) => setShowOnWeb(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                  Show this project on the SEQ Formwork website
                </span>
              </label>
              <label className="form-label" htmlFor="webDescription">
                Website description
              </label>
              <WebDescriptionEditor value={webDescription} onChange={setWebDescription} />
              <p className="web-editor__hint">
                Public marketing copy shown on the website. Write naturally using the toolbar, no formatting
                codes needed. Keep it public-safe: no phone numbers, emails or PO numbers. (Your internal
                Project Details above are never shown on the website.)
              </p>

              <details style={{ marginTop: "var(--spacing-md)" }}>
                <summary style={{ cursor: "pointer", fontSize: "0.85rem", color: "var(--text-secondary)", fontWeight: 500 }}>
                  Website display options (optional)
                </summary>
                <p className="web-editor__hint" style={{ marginTop: 4 }}>
                  Leave blank to auto-fill from the job location and address.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "var(--spacing-md)", marginTop: "var(--spacing-sm)" }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="webTitle">Display name</label>
                    <input id="webTitle" className="form-input" value={webTitle} onChange={(e) => setWebTitle(e.target.value)} placeholder="e.g. Retreat" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="webSuburb">Suburb</label>
                    <input id="webSuburb" className="form-input" value={webSuburb} onChange={(e) => setWebSuburb(e.target.value)} placeholder="e.g. Palm Beach" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="webRegion">Region</label>
                    <select id="webRegion" className="form-input" value={webRegion} onChange={(e) => setWebRegion(e.target.value)}>
                      <option value="">Auto</option>
                      <option value="Brisbane">Brisbane</option>
                      <option value="Gold Coast">Gold Coast</option>
                      <option value="Sunshine Coast">Sunshine Coast</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="webType">Type</label>
                    <input id="webType" className="form-input" value={webType} onChange={(e) => setWebType(e.target.value)} placeholder="e.g. Apartments" />
                  </div>
                </div>
              </details>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: "var(--spacing-lg)",
                alignItems: "start",
              }}
            >
              <div className="form-group">
                <label htmlFor="normalRate" className="form-label">
                  Normal Day Labour Rate ($)
                </label>
                <input data-seq-private
                  id="normalRate"
                  type="number"
                  className="form-input"
                  value={normalRate}
                  onChange={(e) => setNormalRate(e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label htmlFor="overtimeRate" className="form-label">
                  Overtime Day Labour Rate ($)
                </label>
                <input data-seq-private
                  id="overtimeRate"
                  type="number"
                  className="form-input"
                  value={overtimeRate}
                  onChange={(e) => setOvertimeRate(e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>

              {/* SEQ-169 follow up: claim day and payment period are
                  commercial terms, so they are shown only to a user with
                  financial access. The insert above is skipped for everyone
                  else and the RLS policies from 20260915000200 refuse it
                  regardless, so hiding the inputs is the display half only. */}
              {hasFinancialAccess && (
                <>
                <div className="form-group">
                  <label htmlFor="claimDay" className="form-label">
                    Claim Day (1-31)
                  </label>
                  <input data-seq-private
                    id="claimDay"
                    type="number"
                    className="form-input"
                    value={claimDay}
                    onChange={(e) => setClaimDay(e.target.value)}
                    min="1"
                    max="31"
                    step="1"
                  />
                  <p
                    style={{
                      fontSize: "0.7rem",
                      color: "var(--text-muted)",
                      marginTop: 4,
                    }}
                  >
                    Day of month each claim defaults to. Clamped to the end of
                    shorter months.
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="paymentPeriodDays" className="form-label">
                    Payment Period (days)
                  </label>
                  <input data-seq-private
                    id="paymentPeriodDays"
                    type="number"
                    className="form-input"
                    value={paymentPeriodDays}
                    onChange={(e) => setPaymentPeriodDays(e.target.value)}
                    min="0"
                    max="365"
                    step="1"
                  />
                  <p
                    style={{
                      fontSize: "0.7rem",
                      color: "var(--text-muted)",
                      marginTop: 4,
                    }}
                  >
                    Calendar days from claim date to due date.
                  </p>
                </div>
                </>
              )}

              <div className="form-group">
                <label htmlFor="saturdayRate" className="form-label">
                  Saturday Non-Contract Rate ($)
                </label>
                <input data-seq-private
                  id="saturdayRate"
                  type="number"
                  className="form-input"
                  value={saturdayRate}
                  onChange={(e) => setSaturdayRate(e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label htmlFor="saturdayContractOTRate" className="form-label">
                  Saturday Contract OT Rate ($)
                </label>
                <input data-seq-private
                  id="saturdayContractOTRate"
                  type="number"
                  className="form-input"
                  placeholder={overtimeRate || "Uses Overtime Rate"}
                  value={saturdayContractOTRate}
                  onChange={(e) => setSaturdayContractOTRate(e.target.value)}
                  min="0"
                  step="0.01"
                />
                <p
                  style={{
                    fontSize: "0.7rem",
                    color: "var(--text-muted)",
                    marginTop: 4,
                  }}
                >
                  Defaults to Overtime Rate if empty
                </p>
              </div>
            </div>

            {/* Retention Ceiling Base */}
            <div
              style={{
                gridColumn: "1 / -1",
                padding: "var(--spacing-md)",
                background: "var(--bg-tertiary)",
                borderRadius: "var(--border-radius-md)",
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing-md)",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--spacing-md)", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <p style={{ fontWeight: 600, fontSize: "0.875rem", marginBottom: 2 }}>
                    Retention Ceiling Base
                  </p>
                  <p style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                    The retention ceiling % is applied to this base
                  </p>
                </div>

                {/* 3-option pill selector */}
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    borderRadius: "8px",
                    background: "var(--bg-secondary, #f1f5f9)",
                    padding: "4px",
                    flexShrink: 0,
                    alignItems: "center",
                  }}
                >
                  {([
                    { key: 'original' as const, label: 'Original' },
                    { key: 'revised' as const, label: 'Revised' },
                    { key: 'custom' as const, label: 'Custom' },
                  ]).map((opt) => {
                    const isActive = retentionCeilingMode === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setRetentionCeilingMode(opt.key)}
                        style={{
                          padding: "6px 14px",
                          fontSize: "0.75rem",
                          fontWeight: isActive ? 600 : 500,
                          border: "none",
                          borderRadius: "6px",
                          background: isActive ? "var(--bg-primary, #ffffff)" : "transparent",
                          color: isActive ? "var(--text-primary)" : "var(--text-muted)",
                          boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom amount input */}
              {retentionCeilingMode === 'custom' && (
                <div style={{
                  paddingTop: "var(--spacing-md)",
                  borderTop: "1px solid var(--border-color)",
                  marginTop: 4
                }}>
                  <label className="form-label" style={{ fontSize: "0.80rem", fontWeight: 500, marginBottom: 8, display: "block" }}>
                    Locked-In Contract Value Base
                  </label>
                  <div style={{ position: "relative", maxWidth: "300px" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: "0.875rem" }}>$</span>
                    <input data-seq-private
                      type="number"
                      className="form-input"
                      placeholder="e.g. 5,000,000"
                      value={retentionCustomAmount}
                      onChange={(e) => setRetentionCustomAmount(e.target.value)}
                      style={{ paddingLeft: 24, width: "100%" }}
                    />
                  </div>
                  {retentionCustomAmount && (
                    <div style={{ marginTop: 12, padding: "8px 12px", background: "var(--bg-primary, #ffffff)", borderRadius: 6, border: "1px solid var(--border-color)", display: "inline-block" }}>
                      <p style={{ fontSize: "0.75rem", color: "var(--text-secondary)", margin: 0 }}>
                        Retention ceiling ({formatRetentionCeilingPercent(retentionCeilingPercent)}%): <span data-seq-private="" style={{ fontWeight: 600, color: "var(--text-primary)" }}>${retentionCeilingAmount(parseFloat(retentionCustomAmount) || 0, retentionCeilingPercent).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </p>
                    </div>
                  )}
                </div>
              )}

            {/* SEQ-235: ceiling percentage, next to the base control */}
            <div style={{ paddingTop: "var(--spacing-md)", borderTop: "1px solid var(--border-color)", marginTop: 4, marginBottom: "var(--spacing-md)" }}>
              <label htmlFor="new-retention-ceiling-percent" className="form-label" style={{ fontSize: "0.80rem", fontWeight: 500, marginBottom: 8, display: "block" }}>
                Retention ceiling %
              </label>
              <div style={{ position: "relative", maxWidth: "140px" }}>
                <input
                  id="new-retention-ceiling-percent"
                  data-seq-private
                  type="number"
                  inputMode="decimal"
                  className="form-input form-input--percent"
                  min={0.5}
                  max={100}
                  step={0.5}
                  value={retentionCeilingPercent}
                  readOnly={!hasFinancialAccess}
                  aria-readonly={!hasFinancialAccess}
                  aria-describedby="new-retention-ceiling-percent-help"
                  onChange={(e) => setRetentionCeilingPercent(e.target.value)}
                  style={{ paddingRight: 28, width: "100%" }}
                />
                <span aria-hidden="true" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: "0.875rem" }}>%</span>
              </div>
              <p id="new-retention-ceiling-percent-help" style={{ fontSize: "0.75rem", color: "var(--text-muted)", margin: "6px 0 0" }}>
                {hasFinancialAccess ? "Share of the base that retention held can reach. Usually 5." : "Only a user with financial access can change this."}
              </p>
            </div>
            </div>

            {/* Status */}
            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Project Status
              </label>
              <select
                id="status"
                className="form-input"
                value={status}
                onChange={(e) =>
                  setStatus(
                    e.target.value as "active" | "completed" | "on_hold" | "upcoming",
                  )
                }
              >
                <option value="active">Active</option>
                <option value="upcoming">Upcoming</option>
                <option value="completed">Completed</option>
                <option value="on_hold">On Hold</option>
              </select>
            </div>

            {/* SEQ-180 phase 2, P22: project people (free text + datalist). */}
            <ProjectPeopleFields
              idPrefix="new"
              projectManager={projectManager}
              siteManager={siteManager}
              onProjectManagerChange={setProjectManager}
              onSiteManagerChange={setSiteManager}
            />

            {/* Builder Details Section */}
            <div
              style={{
                marginTop: "var(--spacing-lg)",
                padding: "var(--spacing-lg)",
                background: "var(--bg-tertiary)",
                borderRadius: "var(--border-radius-lg)",
              }}
            >
              <h4 style={{ marginBottom: "var(--spacing-md)" }}>
                Builder Details
              </h4>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "var(--spacing-lg)",
                }}
              >
                <div className="form-group">
                  <label htmlFor="builderName" className="form-label">
                    Builder Name
                  </label>
                  <input
                    id="builderName"
                    type="text"
                    className="form-input"
                    placeholder="e.g., Acme Construction Pty Ltd"
                    value={builderName}
                    onChange={(e) => setBuilderName(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="builderEmail" className="form-label">
                    Builder Email
                  </label>
                  <input
                    id="builderEmail"
                    type="email"
                    className="form-input"
                    placeholder="e.g., contact@builder.com"
                    value={builderEmail}
                    onChange={(e) => setBuilderEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="builderAddress" className="form-label">
                  Builder Address
                </label>
                <input
                  id="builderAddress"
                  type="text"
                  className="form-input"
                  placeholder="e.g., 123 Main St, Brisbane QLD 4000"
                  value={builderAddress}
                  onChange={(e) => setBuilderAddress(e.target.value)}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="builderPocName" className="form-label">
                  Builder POC (Point of Contact) Name
                </label>
                <input
                  id="builderPocName"
                  type="text"
                  className="form-input"
                  placeholder="e.g., John Smith"
                  value={builderPocName}
                  onChange={(e) => setBuilderPocName(e.target.value)}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "var(--spacing-lg)",
              }}
            >
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setStep("categories")}
                disabled={!jobLocation || !jobAddress}
              >
                Continue to Categories
              </button>
            </div>
          </div>
        )}

        {step === "categories" && (
          <>
            {/* Contract Value Summary */}
            <div
              className="metric-card"
              style={{ marginBottom: "var(--spacing-xl)" }}
            >
              <div className="metric-label">Total Contract Value</div>
              <div data-seq-private="" className="metric-value">
                {formatCurrency(calculateTotalContractValue())}
              </div>
            </div>

            {/* Categories */}
            {categories.map((category, catIndex) => (
              <div
                key={catIndex}
                className="card"
                style={{ marginBottom: "var(--spacing-lg)" }}
              >
                {/* Category Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "var(--spacing-md)",
                    gap: "var(--spacing-md)",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--spacing-md)",
                    }}
                  >
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Category Name"
                      value={category.name}
                      onChange={(e) =>
                        handleCategoryChange(catIndex, "name", e.target.value)
                      }
                      style={{
                        fontWeight: 600,
                        fontSize: "var(--font-size-lg)",
                        maxWidth: 250,
                      }}
                    />
                    <span data-seq-private=""
                      style={{
                        color: "var(--color-primary)",
                        fontWeight: 600,
                        fontSize: "var(--font-size-lg)",
                      }}
                    >
                      {formatCurrency(getCategoryTotal(category))}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "var(--spacing-sm)",
                      alignItems: "center",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: "var(--font-size-sm)",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={category.isCustomAmountOnly}
                        onChange={() => handleToggleCategoryMode(catIndex)}
                      />
                      Fixed Amount
                    </label>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => handleRemoveCategory(catIndex)}
                      style={{ color: "var(--color-error)" }}
                      title="Remove Category"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>

                {category.isCustomAmountOnly ? (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Total Amount ($)</label>
                    <input data-seq-private
                      type="number"
                      className="form-input"
                      value={category.customAmount}
                      onChange={(e) =>
                        handleCategoryChange(
                          catIndex,
                          "customAmount",
                          e.target.value,
                        )
                      }
                      min="0"
                      step="0.01"
                      style={{ maxWidth: 200 }}
                    />
                  </div>
                ) : (
                  <>
                    {category.subcategories.length > 0 && (
                      <div className="table-container">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Level / Subcategory</th>
                              <th style={{ width: 150 }}>Amount ($)</th>
                              <th style={{ width: 50 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {category.subcategories.map((sub, subIndex) => (
                              <tr key={subIndex}>
                                <td>
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={sub.name}
                                    onChange={(e) =>
                                      handleSubcategoryChange(
                                        catIndex,
                                        subIndex,
                                        "name",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Level name"
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      padding: 0,
                                    }}
                                  />
                                </td>
                                <td>
                                  <input data-seq-private
                                    type="number"
                                    className="form-input"
                                    value={sub.amount}
                                    onChange={(e) =>
                                      handleSubcategoryChange(
                                        catIndex,
                                        subIndex,
                                        "amount",
                                        e.target.value,
                                      )
                                    }
                                    min="0"
                                    step="0.01"
                                    placeholder="0"
                                  />
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() =>
                                      handleRemoveSubcategory(
                                        catIndex,
                                        subIndex,
                                      )
                                    }
                                    style={{
                                      color: "var(--color-error)",
                                      padding: 4,
                                    }}
                                    title="Remove"
                                  >
                                    <X size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Add Subcategory Controls */}
                    <div
                      style={{
                        marginTop: "var(--spacing-md)",
                        display: "flex",
                        gap: "var(--spacing-sm)",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <select
                        className="form-input"
                        style={{ width: "auto", minWidth: 150 }}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleAddSubcategoryFromList(
                              catIndex,
                              e.target.value,
                            );
                            e.target.value = "";
                          }
                        }}
                        defaultValue=""
                      >
                        <option value="">Add from list...</option>
                        {DEFAULT_SUBCATEGORIES.filter(
                          (name) =>
                            !category.subcategories.some(
                              (s) => s.name === name,
                            ),
                        ).map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                      <span
                        style={{
                          color: "var(--text-muted)",
                          fontSize: "var(--font-size-sm)",
                        }}
                      >
                        or
                      </span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleAddSubcategory(catIndex)}
                      >
                        <Plus size={16} />
                        Add Custom Level
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}

            <button
              type="button"
              className="btn btn-secondary w-full"
              onClick={handleAddCategory}
              style={{ marginBottom: "var(--spacing-xl)" }}
            >
              <Plus size={18} />
              Add Category
            </button>

            {importSuccess && (
              <div
                style={{
                  padding: "var(--spacing-md)",
                  background: "rgba(16, 185, 129, 0.1)",
                  border: "1px solid var(--color-success)",
                  borderRadius: "var(--border-radius-md)",
                  marginBottom: "var(--spacing-lg)",
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--spacing-sm)",
                }}
              >
                <CheckCircle
                  size={20}
                  style={{ color: "var(--color-success)" }}
                />
                <p style={{ color: "var(--color-success)" }}>{importSuccess}</p>
              </div>
            )}

            {error && (
              <div
                style={{
                  padding: "var(--spacing-md)",
                  background: "rgba(239, 68, 68, 0.1)",
                  border: "1px solid var(--color-error)",
                  borderRadius: "var(--border-radius-md)",
                  marginBottom: "var(--spacing-lg)",
                }}
              >
                <p style={{ color: "var(--color-error)" }}>{error}</p>
              </div>
            )}

            {/* Discounts Section */}
            <div
              className="card"
              style={{
                marginBottom: "var(--spacing-xl)",
                marginTop: "var(--spacing-lg)",
                borderLeft: "4px solid var(--color-warning)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "var(--spacing-lg)",
                }}
              >
                <h3>Project Discounts</h3>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAddDiscount}
                  disabled={loading}
                >
                  <Plus size={16} style={{ marginRight: 6 }} />
                  Add Discount
                </button>
              </div>

              {discounts.length === 0 ? (
                <p
                  style={{
                    color: "var(--text-secondary)",
                    fontStyle: "italic",
                  }}
                >
                  No discounts added. Click "Add Discount" to apply a discount
                  to the contract value.
                </p>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--spacing-md)",
                  }}
                >
                  {discounts.map((discount, index) => (
                    <div
                      key={index}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr auto",
                        gap: "var(--spacing-md)",
                        alignItems: "end",
                        padding: "var(--spacing-md)",
                        background: "var(--bg-tertiary)",
                        borderRadius: "var(--radius-md)",
                      }}
                    >
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "0.75rem", marginBottom: 4 }}>
                          Description
                        </label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="e.g. Tender Discount"
                          value={discount.description}
                          onChange={(e) =>
                            handleDiscountChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "0.75rem", marginBottom: 4 }}>
                          Amount ($) - Will be subtracted
                        </label>
                        <input data-seq-private
                          type="number"
                          className="form-input"
                          placeholder="0.00"
                          value={discount.amount}
                          onChange={(e) =>
                            handleDiscountChange(
                              index,
                              "amount",
                              e.target.value,
                            )
                          }
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => handleRemoveDiscount(index)}
                        style={{
                          color: "var(--text-muted)",
                          padding: "8px",
                        }}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Total Summary Footer */}
            <div
              className="card"
              style={{
                position: "sticky",
                bottom: "var(--spacing-lg)",
                marginTop: "var(--spacing-xl)",
                border: "2px solid var(--color-primary)",
                background: "var(--bg-primary)",
                boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
                zIndex: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: "var(--text-secondary)",
                      marginBottom: 4,
                    }}
                  >
                    Total Contract Value
                  </div>
                  <div data-seq-private=""
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "var(--color-primary)",
                    }}
                  >
                    {formatCurrency(calculateTotalContractValue())}
                  </div>
                  {discounts.length > 0 && (
                    <div data-seq-private=""
                      style={{
                        fontSize: "0.75rem",
                        color: "var(--color-warning)",
                      }}
                    >
                      Includes -
                      {formatCurrency(
                        discounts.reduce(
                          (sum, d) => sum + (parseFloat(d.amount) || 0),
                          0,
                        ),
                      )}{" "}
                      discount
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: "var(--spacing-md)" }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setStep("details")}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                    style={{ padding: "var(--spacing-md) var(--spacing-xl)" }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={20} />
                        Creating Project...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={20} style={{ marginRight: 8 }} />
                        Create Project
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </form>

      {/* Guide Modal */}
      <TemplateGuideModal
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
        availableProjects={locations}
        isLoadingProjects={loadingLocations}
      />

      {/* Confirmation Modal */}
      {importedData && (
        <ProjectConfirmationModal
          isOpen={isConfirmationOpen}
          importedProjectName={importedData.job_location}
          availableProjects={locations}
          existingProjectNames={existingNames}
          onConfirm={finalizeImport}
          onCancel={() => {
            setIsConfirmationOpen(false);
            setImportedData(null);
          }}
        />
      )}
    </div>
  );
}
